import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { buildApiUrl } from '../../config/api';
import '../../styles/admin-users.css';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        role: 'all',
        status: 'all'
    });

    const getHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            Authorization: `Bearer ${token}`,
            'x-auth-token': token
        };
    };

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl('/admin/users'), {
                headers: getHeaders()
            });
            setUsers(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            alert(error.response?.data?.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const stats = useMemo(() => ({
        total: users.length,
        active: users.filter((user) => user.status === 'active').length,
        admins: users.filter((user) => user.role === 'admin').length,
        users: users.filter((user) => user.role === 'user').length
    }), [users]);

    const filteredUsers = useMemo(() => {
        const query = filters.search.trim().toLowerCase();

        return users.filter((user) => {
            const searchMatch = !query || [user.name, user.email, user.phone]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query));
            const roleMatch = filters.role === 'all' || user.role === filters.role;
            const statusMatch = filters.status === 'all' || user.status === filters.status;

            return searchMatch && roleMatch && statusMatch;
        });
    }, [filters, users]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleUserUpdate = async (user, changes) => {
        try {
            setUpdating(true);
            await axios.put(
                buildApiUrl(`/admin/users/${user.id}/role`),
                changes,
                { headers: getHeaders() }
            );

            setUsers((prev) =>
                prev.map((item) => (item.id === user.id ? { ...item, ...changes } : item))
            );
        } catch (error) {
            console.error('Error updating user:', error);
            alert(error.response?.data?.message || 'Failed to update user');
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async (user) => {
        if (!window.confirm(`Delete ${user.email}? This action cannot be undone.`)) {
            return;
        }

        try {
            setUpdating(true);
            await axios.delete(buildApiUrl(`/admin/users/${user.id}`), {
                headers: getHeaders()
            });

            setUsers((prev) => prev.filter((item) => item.id !== user.id));
        } catch (error) {
            console.error('Error deleting user:', error);
            alert(error.response?.data?.message || 'Failed to delete user');
        } finally {
            setUpdating(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main admin-users-page">
                <AdminHeader
                    title="Users Management"
                    subtitle="Manage customer accounts, roles and account status"
                />

                <section className="users-hero-card">
                    <div className="users-hero-copy">
                        <span className="users-hero-eyebrow">Accounts Center</span>
                        <h2>See who is registered, who is active, and who can access admin tools</h2>
                        <p>Review users, promote admins when needed, and control account status from one clean table.</p>
                    </div>
                    <button type="button" className="toolbar-refresh users-refresh-btn" onClick={fetchUsers}>
                        <i className="fas fa-rotate-right"></i>
                        Refresh
                    </button>
                </section>

                <div className="stats-grid">
                    <div className="stat-card users-stat-card total">
                        <div className="stat-icon"><i className="fas fa-users"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">Total Accounts</span>
                            <span className="stat-value">{stats.total}</span>
                        </div>
                    </div>
                    <div className="stat-card users-stat-card active">
                        <div className="stat-icon"><i className="fas fa-user-check"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">Active Users</span>
                            <span className="stat-value">{stats.active}</span>
                        </div>
                    </div>
                    <div className="stat-card users-stat-card admins">
                        <div className="stat-icon"><i className="fas fa-user-shield"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">Admins</span>
                            <span className="stat-value">{stats.admins}</span>
                        </div>
                    </div>
                    <div className="stat-card users-stat-card customers">
                        <div className="stat-icon"><i className="fas fa-user"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">Customers</span>
                            <span className="stat-value">{stats.users}</span>
                        </div>
                    </div>
                </div>

                <div className="filters-card users-filters-card">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            User Filters
                        </h3>
                    </div>
                    <div className="filters-body">
                        <div className="filters-grid">
                            <div className="filter-group">
                                <label>Search</label>
                                <input
                                    type="text"
                                    name="search"
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    placeholder="Search by name, email or phone"
                                />
                            </div>
                            <div className="filter-group">
                                <label>Role</label>
                                <select name="role" value={filters.role} onChange={handleFilterChange}>
                                    <option value="all">All Roles</option>
                                    <option value="admin">Admin</option>
                                    <option value="user">User</option>
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Status</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange}>
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content-header users-content-header">
                    <div className="results-info">
                        <i className="fas fa-users"></i>
                        <span>Showing <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> users</span>
                    </div>
                </div>

                <div className="admin-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading users...</p>
                        </div>
                    ) : (
                        <div className="table-container users-table-shell">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Phone</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Joined</th>
                                        <th>Last Login</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user) => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div className="user-identity-cell">
                                                        <span className="user-avatar">
                                                            {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                                                        </span>
                                                        <div className="user-copy">
                                                            <strong>{user.name || 'Unnamed User'}</strong>
                                                            <span>{user.email}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{user.phone || 'N/A'}</td>
                                                <td>
                                                    <select
                                                        className="inline-select"
                                                        value={user.role || 'user'}
                                                        disabled={updating}
                                                        onChange={(e) => handleUserUpdate(user, { role: e.target.value })}
                                                    >
                                                        <option value="user">User</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <select
                                                        className="inline-select"
                                                        value={user.status || 'active'}
                                                        disabled={updating}
                                                        onChange={(e) => handleUserUpdate(user, { status: e.target.value })}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </td>
                                                <td>{formatDate(user.created_at)}</td>
                                                <td>{formatDate(user.last_login)}</td>
                                                <td className="actions-cell">
                                                    <button
                                                        type="button"
                                                        className="action-btn delete"
                                                        disabled={updating}
                                                        onClick={() => handleDelete(user)}
                                                        title="Delete User"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center">No users found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminUsers;
